/**
 * Guns (10 §2, missions 15 and 19): the towed howitzer, the sandbagged pit it is fought from, and the
 * coastal casemate. Every muzzle points SOUTH at yaw 0; the casemate's only way in is on its NORTH face.
 */

import type { Pcg32 } from '../../sim/rng';
import type { Palette } from '../palette';
import type { KitBuilder } from './flora';
import { MeshBuilder, place } from './MeshBuilder';
import { strut, wheel } from './parts';
import { prism } from './shapes';
import { capColor, snowSlab } from './snow';
import { bag, BAG_COURSE } from './structures';

/** Barrel elevation above the horizontal, radians: raised, as a howitzer laid on a distant target is. */
const GUN_ELEVATION = 0.42;

/**
 * Towed field howitzer: split trail spread to the north, shield, barrel raised to the south. Steel
 * throughout, so it stands out of the canvas-coloured pit it is dug into.
 */
export const fieldGun: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  const steel = { all: p.color('metal.mid'), top: capColor(p, 'metal.hi') };
  for (const sx of [-1, 1]) wheel(mb, p, [sx * 0.95, 0.6, 0.15], 0.6, 0.22, 8);
  mb.box(1.9, 0.14, 0.14, p.color('metal.lo'), place(0, 0.53, 0.15));
  mb.box(0.5, 0.42, 1.0, steel, place(0, 0.6, 0.1));
  // Shield, raked back a little so its face catches the light.
  mb.box(1.5, 0.95, 0.08, steel, place(0, 0.45, 0.55, 0, 1, -0.2));
  // In the barrel's frame +y runs up the bore: breech, recuperator beneath, tube, muzzle brake.
  mb.with(place(0, 0.95, -0.35, 0, 1, Math.PI / 2 - GUN_ELEVATION), () => {
    mb.box(0.34, 0.42, 0.34, p.color('metal.lo'), place(0, -0.3, 0));
    mb.box(0.2, 1.0, 0.2, p.color('metal.mid'), place(0, 0.1, 0.2));
    mb.cylinder(0.13, 0.1, 2.0, 6, p.color('metal.lo'));
    mb.cylinder(0.17, 0.17, 0.25, 6, p.color('metal.mid'), place(0, 2.0, 0), p.color('ink.0'));
  });
  for (const sx of [-1, 1]) {
    strut(mb, [sx * 0.3, 0.5, -0.05], [sx * 1.15, 0.14, -1.7], 0.17, p.color('metal.mid'));
    mb.box(0.42, 0.34, 0.1, p.color('metal.lo'), place(sx * 1.15, 0, -1.72, sx * -0.47));
  }
  return mb;
};

/** A run of sandbags from one point to another, three courses with a broken crest: `sandbagRun`, aimed. */
function bagRun(mb: MeshBuilder, p: Palette, rng: Pcg32, x0: number, z0: number, x1: number, z1: number): void {
  const length = Math.hypot(x1 - x0, z1 - z0);
  const yaw = Math.atan2(z0 - z1, x1 - x0);
  const n = Math.round(length / 0.74);
  for (let layer = 0; layer < 3; layer++) {
    for (let i = 0; i < n - layer; i++) {
      if (layer === 2 && i % 2 === 1) continue;
      const t = (0.37 + i * 0.74 + layer * 0.37) / length;
      const tone = rng.chance(0.5) ? 'canvas.mid' : 'canvas.hi';
      const top = layer > 0 ? capColor(p, 'canvas.hi') : p.color(tone);
      const at = place(x0 + (x1 - x0) * t, layer * BAG_COURSE, z0 + (z1 - z0) * t, yaw + rng.range(-0.05, 0.05));
      bag(mb, 0.72, 0.46, { all: p.color(tone), top, front: p.color('canvas.lo') }, at);
    }
  }
}

/**
 * Gun pit: a sandbag horseshoe open to the NORTH, over the three boxes the sim blocks, on a pad of
 * trampled earth. The ready ammunition - banded crates, brass cases stood on end - is stacked in the
 * two southern corners, clear of a gun standing anywhere in the middle. It is what cooks off.
 */
export const gunPit: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  mb.box(4.2, 0.1, 3.0, { all: p.color('dirt.lo'), top: p.color('dirt.mid') }, place(0, 0, 0.65));
  bagRun(mb, p, rng, -2.6, 2.4, 2.6, 2.4);
  bagRun(mb, p, rng, -2.3, 2.17, -2.3, -0.8);
  bagRun(mb, p, rng, 2.3, 2.17, 2.3, -0.8);
  const crate = { all: p.color('timber.mid'), top: capColor(p, 'timber.hi') };
  mb.box(0.95, 0.42, 0.55, crate, place(-1.45, 0.08, 1.7));
  mb.box(0.85, 0.4, 0.5, crate, place(-1.5, 0.5, 1.7, 0.2));
  mb.box(0.97, 0.12, 0.57, p.color('enemyAccent.0'), place(-1.45, 0.22, 1.7));
  for (let i = 0; i < 3; i++) {
    const at = place(1.2 + i * 0.26, 0.08, 1.75 + rng.range(-0.06, 0.06));
    mb.cylinder(0.1, 0.1, 0.58, 5, p.color('fire.2'), at, p.color('metal.lo'));
  }
  return mb;
};

/** Plan of the casemate, [x, z]: a thick block with its two southern corners cut back to the embrasure. */
const CASEMATE: ReadonlyArray<readonly [number, number]> = [
  [-3.2, -2.5], [3.2, -2.5], [3.2, 1.7], [2.2, 2.7], [-2.2, 2.7], [-3.2, 1.7],
];
const CASEMATE_ROOF: ReadonlyArray<readonly [number, number]> = [
  [-3.4, -2.7], [3.4, -2.7], [3.4, 1.8], [2.3, 2.95], [-2.3, 2.95], [-3.4, 1.8],
];
export const CASEMATE_WALL_H = 2.3;
export const CASEMATE_ROOF_H = 0.55;

/**
 * Coastal gun casemate (mission 19): nothing but a time bomb opens it, and only at the rear. South
 * face: a stepped embrasure under the roof's overhang with the gun looking out of it. North face:
 * the steel door between two blast walls, which show from above as a notch - where the way in is.
 */
export const casemate: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  prism(mb, CASEMATE, 0, CASEMATE_WALL_H, { top: p.color('concrete.lo'), side: p.color('concrete.mid') });
  const roofTop = CASEMATE_WALL_H + CASEMATE_ROOF_H;
  prism(mb, CASEMATE_ROOF, CASEMATE_WALL_H, roofTop, { top: p.color('concrete.mid'), side: p.color('concrete.hi') });
  // The roof is turfed over for camouflage; in the arctic the same inset patch is the snow.
  if (p.biome === 'arctic') mb.with(place(0, 0, -0.4), () => snowSlab(mb, p, 6.2, 4.2, roofTop));
  else mb.box(4.6, 0.1, 3.0, { all: p.color('dirt.mid'), top: p.color('ground.1') }, place(0, roofTop, -0.5));

  // Embrasure and gun.
  mb.box(2.7, 1.0, 0.1, p.color('concrete.lo'), place(0, 0.95, 2.71));
  mb.box(1.9, 0.62, 0.12, p.color('ink.0'), place(0, 1.14, 2.75));
  mb.with(place(0, 1.42, 2.4, 0, 1, Math.PI / 2 - 0.1), () => {
    mb.cylinder(0.2, 0.16, 1.15, 6, p.color('metal.lo'));
    mb.cylinder(0.22, 0.22, 0.2, 6, p.color('metal.mid'), place(0, 0.95, 0), p.color('ink.0'));
  });
  // Rear door: steel, strapped, the enemy's stripe across it, between two blast walls.
  mb.box(1.1, 1.8, 0.12, p.color('metal.lo'), place(0, 0, -2.55));
  for (const y of [0.45, 1.25]) mb.box(1.16, 0.14, 0.16, p.color('metal.mid'), place(0, y, -2.56));
  mb.box(1.1, 0.16, 0.14, p.color('enemyAccent.0'), place(0, 1.55, -2.56));
  const blastWall = { all: p.color('concrete.lo'), top: capColor(p, 'concrete.mid') };
  for (const sx of [-1, 1]) mb.box(0.4, 1.5, 0.5, blastWall, place(sx * 0.95, 0, -2.75));
  // Roof furniture: a steel observation cupola with its slit to the south, and a vent.
  mb.cylinder(0.62, 0.46, 0.5, 8, p.color('metal.mid'), place(2.0, roofTop, 0.7, Math.PI / 8), p.color('metal.hi'));
  mb.box(0.5, 0.1, 0.06, p.color('ink.0'), place(2.0, roofTop + 0.24, 1.22));
  mb.cylinder(0.24, 0.24, 0.42, 6, p.color('metal.lo'), place(-2.4, roofTop, -1.9), p.color('ink.1'));
  return mb;
};
